import { faSearch } from "@jewlr/pro-light-svg-icons/faSearch"
import loadable from "@loadable/component"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import { Link, useLocation } from "react-router-dom"

import {
  clearSearchUserInput,
  clearSearchSuggestions,
} from "app-store-v2/app/actions"
import AccountIcon from "areas/header/dropdowns/account-icon"
import CartIcon from "areas/header/dropdowns/cart-icon"
import withHeader from "areas/header/hoc/with-header"
import WishlistIcon from "areas/header/jewlr/wishlist-icon"
import NavIcon from "areas/header/nav-icon"
import Button from "components/ui/button"
import Image from "components/ui/image"
import Media from "components/ui/media"
import { isTestMode, retry } from "helpers/application"

import BurgerMenu from "./burger-menu"
import DesktopMenu from "./menu/desktop"

const Search = loadable(() => retry(() => import("./search/search")), {
  ssr: false,
})

const DesktopNav = ({ clearSearch, toggleAccountMenu, toggleMenu }) => {
  const [showSearch, setShowSearch] = useState(false)
  const searchInputRef = useRef()
  const searchBtnRef = useRef()
  const restoreSearchFocus = useRef()
  const location = useLocation()

  useEffect(() => {
    toggleMenu(true)
    toggleAccountMenu(true)
    setShowSearch(false)
    clearSearch()
  }, [location.pathname])

  useEffect(() => {
    if (!showSearch && restoreSearchFocus.current && searchBtnRef.current) {
      searchBtnRef.current.focus()
      restoreSearchFocus.current = false
    }
  }, [showSearch, searchBtnRef.current])

  const toggleSearchBar = (e) => {
    e.preventDefault()
    setShowSearch(!showSearch)
    toggleMenu(true)
    toggleAccountMenu(true)
  }

  const handleCloseSearch = () => {
    restoreSearchFocus.current = true
    setShowSearch(false)
  }

  const trackNavClick = useCallback((navElement) => {
    if (!isTestMode) {
      dataLayer.push({
        element: navElement,
        event: "clicked-nav-element",
      })
    }
  }, [])

  return (
    <Media
      className="desktop:px-24 widescreen:px-32 h-(--desktop-header-height) flex items-center justify-between px-16"
      greaterThanOrEqual="desktop"
    >
      <div className="large:shrink-0 large:grow-0 large:basis-auto">
        <Link
          className="inline-block"
          data-lc="home"
          onClick={() => trackNavClick("logo")}
          to="/"
        >
          <Image
            alt="Jewlr Homepage"
            className="w-(--base-width) widescreen:w-(--widescreen-width)"
            height="139"
            lazyload={false}
            src={require("images/layout/logo/jewlr-logo.svg")}
            style={{
              "--base-width": "150px",
              "--widescreen-width": "170px",
            }}
            width="701"
          />
        </Link>
      </div>
      <DesktopMenu />
      <div className="widescreen:basis-200 basis-160 relative flex shrink-0 grow-0 justify-end gap-24">
        {showSearch ? (
          <Search
            handleCloseSearch={handleCloseSearch}
            searchInputRef={searchInputRef}
          />
        ) : (
          <>
            <AccountIcon dropdown="account" onNavClick={trackNavClick} />
            <WishlistIcon
              dropdown="wishlist"
              onNavClick={() => trackNavClick("wishlist")}
            />
            <Button
              aria-label="Search"
              className="inline-block"
              onClick={(e) => {
                trackNavClick("search")
                toggleSearchBar(e)
              }}
              ref={searchBtnRef}
            >
              <NavIcon icon={faSearch} />
            </Button>
            <CartIcon onNavClick={() => trackNavClick("cart")} />
            <BurgerMenu onNavClick={() => trackNavClick("menu")} />
          </>
        )}
      </div>
    </Media>
  )
}

DesktopNav.propTypes = {
  clearSearch: PropTypes.func,
  toggleAccountMenu: PropTypes.func,
  toggleMenu: PropTypes.func,
}

const mapStateToProps = () => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    clearSearch() {
      dispatch(clearSearchUserInput())
      dispatch(clearSearchSuggestions())
    },
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withHeader(DesktopNav))
